import { defineNuxtPlugin, useRouter } from 'nuxt/app';
import { isBundledNativeOrigin, isNativeApp } from '~/functions/appOrigin';
import { hasPreviousHistoryEntry } from '~/composables/useNativeBackNavigation';

/**
 * Android's hardware / gesture back button.
 *
 * iOS never fires this — it has no back button — so in practice this is Android only, and it
 * is registered on the packaged app alone. The web and the Phase 0 diagnostic keep the
 * browser's own behaviour untouched.
 *
 * ## Why take control rather than leave Capacitor's default
 *
 * `@capacitor/app`'s `AppPlugin.java` registers an `OnBackPressedCallback` that, *when no JS
 * `backButton` listener exists*, calls `webView.goBack()` if the WebView reports it can go
 * back — and does nothing at all if it cannot. Reading that code suggests Android already
 * works, and the first version of this feature deliberately added no listener on that basis.
 *
 * Testing on a real device contradicted it: back closed the app. Rather than keep reasoning
 * about when `webView.canGoBack()` disagrees with the router — the WebView's history and Vue
 * Router's are related but not the same thing, and Capacitor's asset handler sits between
 * them — this takes the decision explicitly. Registering any listener disables that default
 * branch, so the behaviour below is the whole behaviour, which is the point.
 *
 * ## What it does
 *
 * Back inside the app goes back one route. Back with nowhere left to go **exits the app**,
 * which is correct on Android and is what users expect: the platform convention is that back
 * at the root leaves, rather than trapping you. `App.exitApp()` exists for exactly this and
 * its own doc comment says it should only be used with a `backButton` handler.
 */
export default defineNuxtPlugin(() => {
   if (!isNativeApp() || !isBundledNativeOrigin()) return;

   // Captured synchronously: the Nuxt context is not guaranteed after an await.
   const router = useRouter();

   void (async () => {
      const { App } = await import('@capacitor/app');

      await App.addListener('backButton', () => {
         if (hasPreviousHistoryEntry()) {
            router.back();

            return;
         }

         void App.exitApp();
      });
   })();
});
